import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { FileDown, Loader2, Printer } from 'lucide-react';
import { downloadBlob } from '../../../utils/blobDownload';
import { useToast } from '../../../hooks/use-toast';
import { isNativePlatform } from '../../../utils/device';
import PdfEmbed from '../../PdfEmbed';

export default function PortalPdfPreviewModal({
  open,
  onOpenChange,
  title = 'PDF preview',
  blob = null,
  filename = 'document.pdf',
  loading = false,
  error = '',
}) {
  const [pdfUrl, setPdfUrl] = useState('');
  const [downloading, setDownloading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!open || !blob) {
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
      return undefined;
    }

    const url = URL.createObjectURL(blob);
    setPdfUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [open, blob]);

  const handleDownload = async () => {
    if (!blob || downloading) return;
    setDownloading(true);
    try {
      const result = await downloadBlob(blob, filename);
      if (result?.saved && isNativePlatform()) {
        toast({
          title: 'Saved to Downloads',
          description: result.filename || filename,
        });
      }
    } catch (e) {
      toast({
        title: 'Download failed',
        description: e?.message || 'Could not save PDF',
        variant: 'destructive',
      });
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => {
    if (isNativePlatform()) {
      toast({
        title: 'Print not available',
        description: 'Use Download to save this PDF on mobile.',
        variant: 'destructive',
      });
      return;
    }
    if (!pdfUrl) return;
    const w = window.open(pdfUrl, '_blank');
    if (!w) return;
    w.addEventListener('load', () => {
      w.focus();
      w.print();
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] h-[90vh] flex flex-col">
        <DialogHeader className="flex-row items-center justify-between space-y-0 gap-2">
          <DialogTitle className="truncate pr-2">{title}</DialogTitle>
          <div className="flex shrink-0 gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!blob || loading || !!error || downloading}
              onClick={handleDownload}
            >
              {downloading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileDown className="h-4 w-4 mr-2" />
              )}
              Download
            </Button>
            {!isNativePlatform() && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!pdfUrl || loading || !!error}
                onClick={handlePrint}
              >
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            )}
          </div>
        </DialogHeader>
        <div className="flex-1 min-h-0 rounded-lg border overflow-auto bg-white">
          {loading ? (
            <div className="h-full min-h-[200px] flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading PDF preview…
            </div>
          ) : error ? (
            <div className="h-full min-h-[200px] flex items-center justify-center text-red-500 px-4 text-center">
              {error}
            </div>
          ) : (
            <PdfEmbed
              title="pdf-preview"
              blob={blob}
              className="w-full min-h-full bg-white border-0"
              style={{ minHeight: '70vh' }}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
